import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';

interface LoadingCoordinatorState {
  isAuthLoading: boolean;
  isDataLoading: boolean;
  shouldShowLoading: boolean;
  shouldShowSkeleton: boolean;
}

interface LoadingCoordinatorOptions {
  /**
   * 최소 로딩 시간 (ms) - 너무 빠른 깜빡임 방지
   */
  minLoadingTime?: number;
  /**
   * 스켈레톤 모드 사용 여부
   */
  useSkeletonMode?: boolean;
  /**
   * 데이터 로딩 디바운스 시간 (ms)
   */
  debounceTime?: number;
}

/**
 * 인증과 데이터 로딩 상태를 조정하여 깜빡임을 방지하는 훅
 */
export function useLoadingCoordinator(
  dataLoading: boolean,
  options: LoadingCoordinatorOptions = {}
) {
  const {
    minLoadingTime = 300,
    useSkeletonMode = true,
    debounceTime = 100
  } = options;

  const { isLoading: authLoading } = useAuth();
  const [state, setState] = useState<LoadingCoordinatorState>({
    isAuthLoading: authLoading,
    isDataLoading: dataLoading,
    shouldShowLoading: authLoading || dataLoading,
    shouldShowSkeleton: false
  });

  const loadingStartTime = useRef<number | null>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // 디바운스된 상태 업데이트
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      const isCurrentlyLoading = authLoading || dataLoading;
      
      setState(prevState => {
        // 상태가 실제로 변경되었는지 확인하여 불필요한 업데이트 방지
        const needsUpdate = 
          prevState.isAuthLoading !== authLoading ||
          prevState.isDataLoading !== dataLoading ||
          prevState.shouldShowLoading !== isCurrentlyLoading;
        
        if (!needsUpdate) {
          return prevState; // 상태가 동일하면 업데이트하지 않음
        }

        // 로딩 시작 시간 기록
        if (isCurrentlyLoading && !prevState.shouldShowLoading) {
          loadingStartTime.current = Date.now();
        }

        // 로딩 종료 시 최소 시간 확인
        if (!isCurrentlyLoading && prevState.shouldShowLoading && loadingStartTime.current) {
          const elapsed = Date.now() - loadingStartTime.current;
          
          if (elapsed < minLoadingTime) {
            // 최소 시간이 지나지 않았으면 추가 대기
            setTimeout(() => {
              setState(prev => ({
                ...prev,
                isAuthLoading: authLoading,
                isDataLoading: dataLoading,
                shouldShowLoading: false,
                shouldShowSkeleton: false
              }));
              loadingStartTime.current = null;
            }, minLoadingTime - elapsed);
            
            // 현재 상태는 유지
            return prevState;
          }
        }

        // 스켈레톤 모드 결정
        const shouldShowSkeleton = useSkeletonMode && 
                                   !authLoading && 
                                   dataLoading && 
                                   !isCurrentlyLoading;

        if (!isCurrentlyLoading) {
          loadingStartTime.current = null;
        }

        return {
          isAuthLoading: authLoading,
          isDataLoading: dataLoading,
          shouldShowLoading: isCurrentlyLoading,
          shouldShowSkeleton
        };
      });
    }, debounceTime);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [authLoading, dataLoading, minLoadingTime, useSkeletonMode, debounceTime]);

  return {
    /**
     * 인증 로딩 중인지 여부
     */
    isAuthLoading: state.isAuthLoading,
    
    /**
     * 데이터 로딩 중인지 여부
     */
    isDataLoading: state.isDataLoading,
    
    /**
     * 전체 화면 로딩 스피너를 보여줄지 여부
     */
    shouldShowLoading: state.shouldShowLoading,
    
    /**
     * 스켈레톤 화면을 보여줄지 여부
     */
    shouldShowSkeleton: state.shouldShowSkeleton,
    
    /**
     * 콘텐츠를 보여줄지 여부
     */
    shouldShowContent: !state.shouldShowLoading && !state.shouldShowSkeleton,
    
    /**
     * 전체 로딩 상태 (인증 + 데이터)
     */
    isLoading: state.shouldShowLoading
  };
}

/**
 * 스켈레톤 UI 컴포넌트들
 */
export const SkeletonComponents = {
  /**
   * 페이지 헤더 스켈레톤
   */
  PageHeader: () => (
    <div className="flex items-center justify-between p-4 animate-pulse">
      <div className="w-8 h-8 bg-gray-200 rounded"></div>
      <div className="w-24 h-6 bg-gray-200 rounded"></div>
      <div className="w-8 h-8 bg-gray-200 rounded"></div>
    </div>
  ),

  /**
   * 리스트 아이템 스켈레톤
   */
  ListItem: ({ count = 3 }: { count?: number }) => (
    <div className="space-y-4 p-4">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="animate-pulse">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-3 bg-gray-200 rounded w-2/3"></div>
            </div>
            <div className="w-6 h-6 bg-gray-200 rounded"></div>
          </div>
        </div>
      ))}
    </div>
  ),

  /**
   * 카드 리스트 스켈레톤
   */
  CardList: ({ count = 3 }: { count?: number }) => (
    <div className="space-y-4 p-4">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="animate-pulse">
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-start space-x-4">
              <div className="w-16 h-16 bg-gray-200 rounded-lg"></div>
              <div className="flex-1 space-y-2">
                <div className="h-5 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  ),

  /**
   * 이벤트 카드 스켈레톤
   */
  EventCard: ({ count = 3 }: { count?: number }) => (
    <div className="space-y-4 p-4">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="animate-pulse">
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="w-full aspect-[4/3] bg-gray-200"></div>
            <div className="p-4 space-y-3">
              <div className="flex items-center space-x-2">
                <div className="w-12 h-5 bg-gray-200 rounded-full"></div>
                <div className="w-16 h-5 bg-gray-200 rounded-full"></div>
              </div>
              <div className="h-6 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  ),

  /**
   * 공지사항 아코디온 스켈레톤
   */
  AccordionList: ({ count = 4 }: { count?: number }) => (
    <div className="space-y-1">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="animate-pulse">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex-1 space-y-2">
                <div className="h-5 bg-gray-200 rounded"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
              <div className="w-6 h-6 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  ),

  /**
   * 상세 페이지 스켈레톤
   */
  DetailPage: () => (
    <div className="p-4 animate-pulse space-y-6">
      <div className="space-y-4">
        <div className="h-8 bg-gray-200 rounded"></div>
        <div className="h-4 bg-gray-200 rounded w-1/3"></div>
      </div>
      
      <div className="space-y-3">
        {Array.from({ length: 8 }).map((_, index) => (
          <div key={index} className="h-4 bg-gray-200 rounded"></div>
        ))}
      </div>
      
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="h-4 bg-gray-200 rounded w-5/6"></div>
        ))}
      </div>
    </div>
  )
};